/**
 * Verification-link timing and route hand-off for the deferred registration
 * flow (D-049). Registration does not create an account: it stores a pending
 * registration in Redis that expires, and the UI states that window.
 */

/**
 * Documented fallback window (15 minutes) used only when the server value is
 * unavailable, e.g. a direct visit or a page reload that lost the route state.
 * The backend always returns the real TTL in `RegisterResponse.expiresInSeconds`.
 */
export const FALLBACK_VERIFICATION_TTL_SECONDS = 15 * 60

/**
 * Formats the server-provided TTL as whole minutes for user-facing copy.
 * Invalid or missing values fall back to the documented 15-minute window; sub
 * minute values round up so the copy never says "0 minutes".
 */
export function verificationWindowMinutes(
  expiresInSeconds?: number | null,
): number {
  const seconds =
    typeof expiresInSeconds === 'number' &&
    Number.isFinite(expiresInSeconds) &&
    expiresInSeconds > 0
      ? expiresInSeconds
      : FALLBACK_VERIFICATION_TTL_SECONDS

  return Math.max(1, Math.ceil(seconds / 60))
}

/**
 * Route state handed from `/register` to `/verify-registration`. Everything
 * comes from the registration response; `expiresInSeconds` is never hard-coded
 * by the caller.
 */
export interface VerifyRegistrationRouteState {
  email?: string
  emailDeliveryAvailable?: boolean
  expiresInSeconds?: number
}
